export interface ScrapeResult {
  name: string | null;
  description: string | null;
  industry: string | null;
  companySizeSuggestion: string | null;
  targetBuyersHint: string | null;
  ogTitle: string | null;
  ogDescription: string | null;
  ogImage: string | null;
  jsonLd: Record<string, unknown>[] | null;
  /** Canonical URL used when scraping (after https/http and optional www resolution). */
  resolvedUrl?: string;
}

interface CompanyData {
  website: string;
  industry: string;
  revenueModel: string;
  companySize: string;
  marketPosition: string;
}

interface ProductData {
  description: string;
  valueProposition: string;
  onboardingProcess: string;
  cost: string;
  pricingDetails: string;
  topFeedbackItems: string[];
}

interface SalesData {
  targetBuyers: string;
  topBuyerTitles: string[];
  buyerChannels: string;
  salesProcessTimeline: { stage: string; order: number }[];
  salesCycle: string;
  majorObjections: string[];
  objectionHandling: string;
}

export interface ScrapeMapping {
  company: CompanyData;
  product: ProductData;
  sales: SalesData;
  updatedSections: ('company' | 'product' | 'sales')[];
}

function nonEmpty(v: string | null | undefined): v is string {
  return typeof v === 'string' && v.trim().length > 0;
}

/**
 * Derive a short value-proposition line from scrape data that doesn't duplicate
 * the full description. Returns null when there's nothing distinct to offer.
 */
function deriveValueProposition(scrape: ScrapeResult): string | null {
  const full = scrape.description?.trim() ?? '';
  const og = scrape.ogDescription?.trim() ?? '';
  if (nonEmpty(og) && og !== full) return og;
  const title = scrape.ogTitle?.trim() ?? scrape.name?.trim() ?? '';
  if (nonEmpty(title) && title !== full) return title;
  return null;
}

const VALID_SIZES = new Set(['1-10', '11-50', '51-200', '201-500', '500+']);

export function mapScrapeToQuestionnaire(
  scrape: ScrapeResult,
  current: { company: CompanyData; product: ProductData; sales: SalesData },
): ScrapeMapping {
  const company = { ...current.company };
  const product = { ...current.product };
  const sales = { ...current.sales };
  const updatedSections: ('company' | 'product' | 'sales')[] = [];

  let companyChanged = false;
  if (nonEmpty(scrape.industry)) {
    company.industry = scrape.industry;
    companyChanged = true;
  }
  if (nonEmpty(scrape.companySizeSuggestion) && VALID_SIZES.has(scrape.companySizeSuggestion)) {
    company.companySize = scrape.companySizeSuggestion;
    companyChanged = true;
  }
  if (companyChanged) updatedSections.push('company');

  let productChanged = false;
  if (nonEmpty(scrape.description)) {
    product.description = scrape.description;
    productChanged = true;
  }
  const vp = deriveValueProposition(scrape);
  if (nonEmpty(vp)) {
    product.valueProposition = vp;
    productChanged = true;
  }
  if (productChanged) updatedSections.push('product');

  if (nonEmpty(scrape.targetBuyersHint)) {
    sales.targetBuyers = scrape.targetBuyersHint;
    updatedSections.push('sales');
  }

  return { company, product, sales, updatedSections };
}
